import { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/Button';
import Dropdown from '@/components/ui/Dropdown';
import AnnouncementAnchor from '@/components/Announcements/AnnouncementAnchor';
import icExport from '@/assets/icons/export.svg';
import icExportToPDF from '@/assets/icons/exportToPDF.svg';
import icExportToExcel from '@/assets/icons/exportToExcel.svg';
import styles from './ExportTableBtn.module.css';
import { exportToExcel } from '@/helpers/exportTableHelper';

export type ExportFormat = 'excel' | 'pdf';

/**
 * HAT-1493: an entry in the export menu that opens something on the screen instead of handing
 * over a file - the overheads table on the transactions board. It comes after the file formats.
 */
export interface ExportMenuItem {
  key: string;
  label: string;
  icon: string;
  onSelect: () => void;
  /** The announcement anchor for a new entry: the bubble points at the export button, since the entry itself is hidden until the menu opens. */
  announce?: string;
}

interface ExportTableBtnProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  filename: string;
  compact?: boolean;
  /**
   * Optional async provider for the exported rows (e.g., enriching them with extra data
   * fetched on demand). When it fails, nothing is exported - a partial file would look
   * complete and mislead the user.
   */
  getData?: () => Promise<T[]>;
  onExport?: (format: ExportFormat) => void;
  /**
   * HAT-1399: when set, the button opens a choice of Excel or PDF instead of exporting to
   * Excel straight away. The board builds the PDF itself, from the rows it shows.
   */
  onExportPdf?: () => Promise<void>;
  /** Entries after the file formats; with any of them the button opens the menu even without a PDF. */
  extraItems?: ExportMenuItem[];
}

function ExportTableBtn<T>({ data, columns, filename, getData, onExport, onExportPdf, extraItems = [], compact = false }: ExportTableBtnProps<T>) {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const hasMenu = !!onExportPdf || extraItems.length > 0;

  const handleExportToExcel = async () => {
    if (isExporting) return;
    setIsOpen(false);

    let exportData = data;
    if (getData) {
      setIsExporting(true);
      try {
        exportData = await getData();
      } catch (error) {
        console.error('Error preparing export data:', error);
        return;
      } finally {
        setIsExporting(false);
      }
    }

    exportToExcel(exportData, columns, filename, t);
    onExport?.('excel');
  };

  const handleExportToPdf = async () => {
    if (isExporting || !onExportPdf) return;
    setIsOpen(false);
    setIsExporting(true);
    try {
      await onExportPdf();
      onExport?.('pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const trigger = (
    <Button
      variant="white"
      onClick={hasMenu ? undefined : handleExportToExcel}
      disabled={isExporting}
      className={`${styles.exportButton} ${compact ? styles.compact : ''}`}
      aria-label={t('exportTable')}
      aria-haspopup={hasMenu ? 'menu' : undefined}
      aria-expanded={hasMenu ? isOpen : undefined}
      aria-busy={isExporting}
      title={t('exportTable')}
    >
      <img src={icExport} alt="" />
      {/* A PDF takes a second or two to build; the label says the click was taken. */}
      <span className="body-sm-semibold text-(--font-body-5)">{t(isExporting ? 'exportPreparing' : compact ? 'exportTableShort' : 'exportTable')}</span>
    </Button>
  );

  if (!hasMenu) return trigger;

  const menu = (
    <Dropdown trigger={trigger} isOpen={isOpen} setIsOpen={setIsOpen} contentAlign="end" activeTriggerClassName={styles.exportButtonActive}>
      <div className={styles.dropdown} role="menu">
        <Button variant="white" role="menuitem" onClick={handleExportToExcel} className={styles.option}>
          <img src={icExportToExcel} alt="" />
          <span className="font-semibold text-(--gray-5)">{t('exportToExcel')}</span>
        </Button>
        {onExportPdf && (
          <Button variant="white" role="menuitem" onClick={handleExportToPdf} className={styles.option}>
            <img src={icExportToPDF} alt="" />
            <span className="font-semibold text-(--gray-5)">{t('exportToPDF')}</span>
          </Button>
        )}
        {extraItems.map((item) => (
          <Button
            key={item.key}
            variant="white"
            role="menuitem"
            onClick={() => {
              setIsOpen(false);
              item.onSelect();
            }}
            className={styles.option}
          >
            <img src={item.icon} alt="" />
            <span className="font-semibold text-(--gray-5)">{item.label}</span>
          </Button>
        ))}
      </div>
    </Dropdown>
  );

  const announce = extraItems.find((item) => item.announce)?.announce;
  return announce ? <AnnouncementAnchor name={announce}>{menu}</AnnouncementAnchor> : menu;
}

export default ExportTableBtn;
